import React, { useState, useEffect, useMemo, useRef } from 'react';
import PlayerContextMenu from './PlayerContextMenu';

const TABS = [
  { key: 'area',       label: 'Local',      icon: '✨' },
  { key: 'global',     label: 'World',      icon: '🌐' },
  { key: 'guild',      label: 'Guild',      icon: '⚔️' },
  { key: 'fellowship', label: 'Fellowship', icon: '🤝' },
  { key: 'whisper',    label: 'DMs',        icon: '💬' },
];

// Local "hide this message" list (per character, capped at 500 ids, FIFO).
const HIDDEN_KEY = (characterId) => `loria:hidden_msg:${characterId || 'anon'}`;
const HIDDEN_MAX = 500;
function loadHidden(characterId) {
  try {
    const raw = localStorage.getItem(HIDDEN_KEY(characterId));
    return new Set(raw ? JSON.parse(raw) : []);
  } catch { return new Set(); }
}
function saveHidden(characterId, set) {
  try {
    let arr = Array.from(set);
    if (arr.length > HIDDEN_MAX) arr = arr.slice(-HIDDEN_MAX);
    localStorage.setItem(HIDDEN_KEY(characterId), JSON.stringify(arr));
  } catch { /* quota — silently ignore */ }
}

const ChatPanel = ({ 
  chatMessages = [], 
  onSendMessage,
  character, 
  nearbyPlayers = [],
  chatType = 'area',
  onChatTypeChange,
  whisperTarget,
  onClearWhisperTarget,
  fellowship,
  socket,
  onWhisper,
  onInspect,
  onReport,
  onOpenCoC,
}) => {
  const [messageText, setMessageText] = useState('');
  const chatEndRefs = useRef({});
  const [unreadTabs, setUnreadTabs] = useState({});
  const prevCountRef = useRef({});
  const [hiddenIds, setHiddenIds] = useState(() => loadHidden(character?.id));
  const [contextMenu, setContextMenu] = useState(null); // { x, y, target, messageKey? }
  const [prefs, setPrefs] = useState({ hide_global_chat: false, hide_area_chat: false });

  // Fetch user preferences (visual hide flags) once per mount.
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const API = process.env.REACT_APP_SERVER_URL || '';
    fetch(`${API}/api/preferences`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.preferences) setPrefs(data.preferences); })
      .catch(() => { /* ignore — keep defaults */ });
  }, []);

  // Track new messages arriving on tabs the user is NOT currently viewing
  useEffect(() => {
    const counts = {};
    for (const tab of TABS) {
      counts[tab.key] = chatMessages.filter(m => {
        if (tab.key === 'area') return m.chatType === 'area' || m.chatType === 'action';
        return m.chatType === tab.key;
      }).length;
    }
    // On mount, seed the previous counts so everything starts as "read"
    if (Object.keys(prevCountRef.current).length === 0) {
      prevCountRef.current = counts;
      return;
    }
    // Mark tabs with new messages as unread (except the active tab)
    const newUnread = { ...unreadTabs };
    for (const tab of TABS) {
      const prev = prevCountRef.current[tab.key] || 0;
      if (counts[tab.key] > prev && tab.key !== chatType) {
        newUnread[tab.key] = true;
      }
    }
    setUnreadTabs(newUnread);
    prevCountRef.current = counts;
  }, [chatMessages]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear unread indicator when switching to a tab
  useEffect(() => {
    setUnreadTabs(prev => ({ ...prev, [chatType]: false }));
  }, [chatType]);

  // Auto-scroll the active tab to bottom when new messages arrive
  useEffect(() => {
    const ref = chatEndRefs.current[chatType];
    if (ref) {
      ref.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, chatType]);

  // When a whisper target is set, switch to whisper tab
  useEffect(() => {
    if (whisperTarget) {
      onChatTypeChange('whisper');
    }
  }, [whisperTarget]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendMessage = (e) => {
    e.preventDefault();
    if (!messageText.trim() || !onSendMessage) return;

    if (chatType === 'whisper') {
      if (!whisperTarget) return;
      onSendMessage(messageText.trim(), 'whisper', whisperTarget.id);
    } else if (chatType === 'fellowship') {
      if (socket && fellowship) {
        socket.emit('fellowship_chat', { message: messageText.trim() });
      }
    } else {
      onSendMessage(messageText.trim(), chatType);
    }
    setMessageText('');
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  };

  // Filter messages by current tab (and apply local "hide" list)
  const messageKey = (msg, i) => `${msg.timestamp || ''}|${msg.characterId || msg.characterName || ''}|${i}`;
  // v643.3 (#19): memoize the filtered slice so chat re-renders triggered by
  // unrelated state (typing, focus, hover) don't re-walk the full message list.
  const filteredMessages = useMemo(() => chatMessages.filter((msg, i) => {
    if (hiddenIds.has(messageKey(msg, i))) return false;
    // Visual hide per user preferences (server also filters, but this keeps
    // the UI responsive to toggles without a reconnect).
    if (prefs.hide_global_chat && msg.chatType === 'global') return false;
    if (prefs.hide_area_chat   && msg.chatType === 'area')   return false;
    // System notices (slow mode, verify-email, Code of Conduct, gate rejections) are
    // feedback about the current action — show them in EVERY tab so a blocked message
    // never fails silently.
    if (msg.chatType === 'system' || msg.type === 'system') return true;
    if (chatType === 'area') {
      return msg.chatType === 'area' || msg.chatType === 'action' || msg.chatType === 'system' || msg.isEmote;
    }
    if (chatType === 'fellowship') {
      return msg.chatType === 'fellowship';
    }
    if (chatType === 'whisper') {
      if (msg.chatType !== 'whisper') return false;
      // Show all whispers, or filter to selected conversation
      if (whisperTarget) {
        return (
          (msg.characterId === whisperTarget.id) ||
          (msg.recipientId === whisperTarget.id)
        );
      }
      return true;
    }
    return msg.chatType === chatType;
  }), [chatMessages, hiddenIds, prefs.hide_global_chat, prefs.hide_area_chat, chatType, whisperTarget]);

  const hideMessage = (key) => {
    setHiddenIds(prev => {
      const next = new Set(prev);
      next.add(key);
      saveHidden(character?.id, next);
      return next;
    });
  };

  const openContextMenu = (e, message, key) => {
    if (!message.characterId || !message.characterName) return;
    if (message.chatType === 'system' || message.type === 'system') return;
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      target: { id: message.characterId, name: message.characterName },
      messageKey: key,
    });
  };

  const renderMessage = (message, index) => {
    const isSystem = message.type === 'system' || message.chatType === 'system';
    const isAction = message.chatType === 'action';
    const isEmote = message.isEmote;
    const isWhisper = message.chatType === 'whisper';
    const isSelf = message.characterName === character?.name;
    const msgClass = `chat-msg${isSystem ? ' system' : ''}${isAction ? ' action' : ''}${isEmote ? ' emote' : ''}${isWhisper ? ' whisper' : ''}${isSelf ? ' self' : ''}`;
    const key = messageKey(message, index);

    const nameStyle = message.chatColor ? { color: message.chatColor, cursor: 'context-menu' } : { cursor: 'context-menu' };
    const patronBadge = message.isPatron ? <span className="chat-patron-badge" title="Patron">★</span> : null;
    const titleTag = message.title ? <span className="chat-title-tag">[{message.title}]</span> : null;
    const senderMenuHandler = (e) => openContextMenu(e, message, key);

    return (
      <div key={`${chatType}-${index}-${message.timestamp}`} className={msgClass}>
        <span className="chat-timestamp">{formatTimestamp(message.timestamp)}</span>
        {isWhisper && (
          <span
            className="chat-whisper-tag"
            onContextMenu={!isSelf ? senderMenuHandler : undefined}
            onClick={!isSelf ? senderMenuHandler : undefined}
            title={!isSelf ? 'Right-click / tap for options' : undefined}
          >
            {isSelf ? `To ${message.recipientName || '???'}` : `From ${message.characterName}`}:
          </span>
        )}
        {!isSystem && !isWhisper && (
          <span
            className={`chat-sender${isSelf ? ' self' : ''}`}
            style={nameStyle}
            onContextMenu={senderMenuHandler}
            onClick={!isSelf ? senderMenuHandler : undefined}
            title={!isSelf ? 'Right-click / tap for options' : undefined}
          >
            {patronBadge}{titleTag}{message.characterName || message.sender}:
          </span>
        )}
        <span className="chat-text">
          {message.message || message.content}
          {isSystem && onOpenCoC && /code of conduct/i.test(message.message || '') && (
            <button
              type="button"
              onClick={onOpenCoC}
              style={{ marginLeft: 8, padding: '1px 8px', borderRadius: 5, border: '1px solid #d4af37', background: 'rgba(212,175,55,0.15)', color: '#f0d98c', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 'bold' }}
            >
              Review &amp; Accept →
            </button>
          )}
        </span>
      </div>
    );
  };

  const getPlaceholder = () => {
    switch (chatType) {
      case 'area':       return 'Say something to nearby players...';
      case 'global':     return 'Say something to the whole world...';
      case 'guild':      return character?.guild_id ? 'Say something to your guild...' : 'You are not in a guild';
      case 'fellowship': return fellowship ? 'Say something to your fellowship...' : 'You are not in a fellowship';
      case 'whisper':    return whisperTarget ? `Whisper to ${whisperTarget.name}...` : 'Select a player to whisper to';
      default:           return 'Type a message...';
    }
  };

  const isDisabled = (chatType === 'guild' && !character?.guild_id) ||
                     (chatType === 'fellowship' && !fellowship) ||
                     (chatType === 'whisper' && !whisperTarget);

  return (
    <div className="chat-panel-bottom">
      {/* Tabs */}
      <div className="chat-tabs-bottom">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => onChatTypeChange(tab.key)}
            className={`chat-tab-bottom ${chatType === tab.key ? 'active' : ''}`}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
            {unreadTabs[tab.key] && chatType !== tab.key && (
              <span className="tab-unread-dot" />
            )}
          </button>
        ))}
      </div>

      {/* Whisper target indicator */}
      {chatType === 'whisper' && whisperTarget && (
        <div className="whisper-target-bar">
          <span className="whisper-target-label">
            💬 Chatting with <strong>{whisperTarget.name}</strong>
          </span>
          <button className="whisper-clear-btn" onClick={onClearWhisperTarget}>✕</button>
        </div>
      )}

      {/* Messages */}
      <div className="chat-messages-bottom">
        {filteredMessages.length === 0 && (
          <div className="chat-empty">
            {chatType === 'area' && 'No local messages yet. Say hi to nearby players!'}
            {chatType === 'global' && 'No world messages yet. Be the first to speak!'}
            {chatType === 'guild' && (character?.guild_id 
              ? 'No guild messages yet.' 
              : 'Join a guild to chat with guild members.')}
            {chatType === 'fellowship' && (fellowship
              ? 'No fellowship messages yet.'
              : 'Join a fellowship to chat with your group.')}
            {chatType === 'whisper' && (whisperTarget
              ? `No messages with ${whisperTarget.name} yet.`
              : 'Click "Whisper" on an online player to start a DM.')}
          </div>
        )}
        {filteredMessages.map((msg, i) => renderMessage(msg, i))}
        <div ref={el => chatEndRefs.current[chatType] = el} />
      </div>

      {/* Input */}
      <form onSubmit={sendMessage} className="chat-input-bottom">
        <input
          type="text"
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          placeholder={getPlaceholder()}
          className="chat-input-field"
          disabled={isDisabled}
        />
        <button
          type="submit"
          disabled={!messageText.trim() || isDisabled}
          className="chat-send"
        >
          Send
        </button>
      </form>

      {contextMenu && (
        <PlayerContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          target={contextMenu.target}
          selfCharacterId={character?.id}
          onClose={() => setContextMenu(null)}
          onWhisper={onWhisper}
          onInspect={onInspect}
          onReport={onReport ? (target) => onReport(target, chatMessages) : undefined}
          onHideMessage={contextMenu.messageKey ? () => hideMessage(contextMenu.messageKey) : undefined}
        />
      )}
    </div>
  );
};

export default ChatPanel;
